/**
 * volumeRegistry.js — Реестр томов с LRU-вытеснением
 * Mikhmel Cornerstone4D DICOM Viewer Pro v6
 *
 * [v2.0 — ROBUST REGISTRY]:
 *  - setMaxRecords(n): настройка лимита
 *  - hasVolume(id): проверка существования
 *  - getVolumeStats(): статистика по типам и кадрам
 *  - isProtected(imageId): проверка защиты кадра
 *  - touchVolume(id): обновление LRU-позиции
 *  - exportRegistry(): экспорт для диагностики
 *  - Стабильная сортировка в planRestore (a.at - b.at)
 *  - onChange коллбэк для подписки на изменения
 *
 * Почему появился. В 7.0.58/59 запоминалась одна запись — _lastVolumeLoad. На
 * устройстве отчёт показал «СЛУЧАЙ C, томов: 3»: из кэша пропали три тома, а
 * восстановить по одной записи можно было ровно один. Остальные два оставались
 * мёртвыми, и при этом отчёт бодро сообщал «Перезагружен из исходных снимков».
 *
 * Модуль намеренно без браузерных зависимостей: это просто учёт и правило
 * «что из пропавшего мы умеем восстановить». Проверяется целиком.
 */

const PREFIX = 'cornerstoneStreamingImageVolume:';
const PREFIX_LEN = PREFIX.length;

const _records = new Map();
let _maxRecords = 12;
let _onEvict = null;
let _onChange = null;

// ══════════════════════════════════════════════════════════════
//  КОНФИГУРАЦИЯ
// ══════════════════════════════════════════════════════════════

export function setMaxRecords(n) {
  _maxRecords = Math.max(1, Math.min(100, n));
  while (_records.size > _maxRecords) _evictOldest();
}

export function setEvictionHandler(fn) {
  _onEvict = typeof fn === 'function' ? fn : null;
}

/**
 * Подписка на изменения реестра.
 * @param {Function} fn — (event, record) => void
 *   event: 'added' | 'removed' | 'updated' | 'evicted' | 'cleared'
 */
export function onRegistryChange(fn) {
  _onChange = typeof fn === 'function' ? fn : null;
}

// ══════════════════════════════════════════════════════════════
//  HELPERS
// ══════════════════════════════════════════════════════════════

function _evicted(rec) {
  if (rec && _onEvict) {
    try { _onEvict(rec); } catch (e) { /* */ }
  }
}

function _notify(event, rec) {
  if (_onChange) {
    try { _onChange(event, rec); } catch (e) { /* */ }
  }
}

function _evictOldest() {
  const oldestKey = _records.keys().next().value;
  const oldest = _records.get(oldestKey);
  _records.delete(oldestKey);
  _evicted(oldest);
  _notify('evicted', oldest);
  return oldest;
}

// ══════════════════════════════════════════════════════════════
//  CORE API
// ══════════════════════════════════════════════════════════════

export function canonicalId(volumeId) {
  const s = String(volumeId || '');
  return s.startsWith(PREFIX) ? s.slice(PREFIX_LEN) : s;
}

export function sameVolume(a, b) {
  return canonicalId(a) === canonicalId(b);
}

export function rememberVolume(volumeId, imageIds, viewportIds, opts = {}, kind = 'main') {
  const id = canonicalId(volumeId);
  if (!id || !Array.isArray(imageIds) || !imageIds.length) return null;

  const existed = _records.has(id);
  const prev = _records.get(id);

  const rec = {
    volumeId: id,
    imageIds: imageIds.slice(),
    viewportIds: Array.isArray(viewportIds) ? viewportIds.slice() : [],
    opts: { ...opts },
    kind,
    at: existed ? prev.at : _records.size,
    createdAt: prev?.createdAt || Date.now(),
    updatedAt: Date.now(),
    accessCount: (prev?.accessCount || 0) + 1,
  };

  _records.delete(id);
  _records.set(id, rec);

  _notify(existed ? 'updated' : 'added', rec);

  // Старые кадры больше не защищаем
  if (prev) {
    const now = new Set(rec.imageIds);
    const dropped = prev.imageIds.filter(i => !now.has(i));
    if (dropped.length) _evicted({ ...prev, imageIds: dropped });
  }

  while (_records.size > _maxRecords) _evictOldest();

  return rec;
}

export function forgetVolume(volumeId) {
  const id = canonicalId(volumeId);
  const rec = _records.get(id);
  const had = _records.delete(id);
  if (had) {
    _evicted(rec);
    _notify('removed', rec);
  }
  return had;
}

export function forgetAllVolumes() {
  const all = [..._records.values()];
  _records.clear();
  for (const rec of all) _evicted(rec);
  _notify('cleared', null);
}

export function getVolumeRecord(volumeId) {
  return _records.get(canonicalId(volumeId)) || null;
}

export function hasVolume(volumeId) {
  return _records.has(canonicalId(volumeId));
}

export function knownVolumeIds() {
  return [..._records.keys()];
}

export function volumeCount() {
  return _records.size;
}

/**
 * Обновить LRU-позицию тома (переместить в конец).
 * Полезно при обращении к тому — он не будет вытеснен следующим.
 */
export function touchVolume(volumeId) {
  const id = canonicalId(volumeId);
  const rec = _records.get(id);
  if (!rec) return;

  _records.delete(id);
  _records.set(id, {
    ...rec,
    at: _records.size,
    accessCount: rec.accessCount + 1,
    updatedAt: Date.now(),
  });
}

export function allImageIds() {
  const out = [];
  for (const r of _records.values()) out.push(...r.imageIds);
  return out;
}

/**
 * Проверить, защищён ли кадр от вытеснения (принадлежит известному тому).
 */
export function isProtected(imageId) {
  for (const r of _records.values()) {
    if (r.imageIds.includes(imageId)) return true;
  }
  return false;
}

export function planRestore(missingIds = []) {
  const restorable = [];
  const unrestorable = [];

  for (const raw of missingIds) {
    const rec = getVolumeRecord(raw);
    if (rec && rec.imageIds.length) restorable.push(rec);
    else unrestorable.push(String(raw));
  }

  // Стабильная сортировка: main первее, затем по at
  restorable.sort((a, b) => {
    const kindDiff = (a.kind === 'main' ? -1 : 0) - (b.kind === 'main' ? -1 : 0);
    if (kindDiff !== 0) return kindDiff;
    return a.at - b.at;
  });

  return { restorable, unrestorable };
}

// ══════════════════════════════════════════════════════════════
//  ДИАГНОСТИКА
// ══════════════════════════════════════════════════════════════

/**
 * Статистика реестра.
 * @returns {{ total: number, main: number, overlay: number, totalFrames: number, oldestAt: number }}
 */
export function getVolumeStats() {
  let mainCount = 0, overlayCount = 0, totalFrames = 0;
  let oldestAt = Infinity, newestAt = -1;

  for (const r of _records.values()) {
    if (r.kind === 'main') mainCount++;
    else overlayCount++;
    totalFrames += r.imageIds.length;
    if (r.at < oldestAt) oldestAt = r.at;
    if (r.at > newestAt) newestAt = r.at;
  }

  return {
    total: _records.size,
    main: mainCount,
    overlay: overlayCount,
    totalFrames,
    oldestAt: oldestAt === Infinity ? null : oldestAt,
    newestAt: newestAt === -1 ? null : newestAt,
    maxRecords: _maxRecords,
  };
}

/**
 * Экспорт реестра для диагностики.
 * @returns {Object[]}
 */
export function exportRegistry() {
  return [..._records.values()].map(r => ({
    volumeId: r.volumeId,
    kind: r.kind,
    frameCount: r.imageIds.length,
    viewportCount: r.viewportIds.length,
    accessCount: r.accessCount,
    createdAt: r.createdAt,
    updatedAt: r.updatedAt,
    at: r.at,
  }));
}

export default {
  canonicalId, sameVolume,
  rememberVolume, forgetVolume, forgetAllVolumes,
  getVolumeRecord, hasVolume, knownVolumeIds, volumeCount,
  touchVolume, allImageIds, isProtected, planRestore,
  getVolumeStats, exportRegistry,
  setMaxRecords, setEvictionHandler, onRegistryChange,
};